import { Brand, createBranded } from '@deltasierra/type-utilities';
import { FullAuditable, HasId } from '../common/model/traits';
import { Client } from '../client/Client';
import { ClientId } from '../client/ClientId';
import { InviteAgencyUserDto, SanitizedUser, UpdateManagerDto, User, UserId } from '../user/User';
import { ObjectOrVoid, PathBuilder, ApiRoute, HttpMethod } from '../common/api/routes';
import { AgencyRole } from './AgencyClientRole';
import { UploadId } from '../upload/Upload';

export interface Agency extends HasId<AgencyId>, FullAuditable {
    company: string;
    googleAdWordsMarketingEmailAddress: string | null;
    localeCode: string | null;
    whitelabelBannerLogoUploadId: UploadId | null;
    notificationEmailAddresses: string[];
    specialRequestDisclaimer: string | null;

    users?: User[];
    clients?: Client[];
}

export type AgencyContactDetails = Pick<Agency, 'googleAdWordsMarketingEmailAddress' | 'specialRequestDisclaimer'>;

export type AgencySpecialRequestDisclaimer = Pick<Agency, 'specialRequestDisclaimer'>;

export interface NewAgencyDto {
    company: string;
    localeCode: string | null;
}

export interface UpdateAgencyDto {
    localeCode?: string | null;
    whitelabelBannerLogoUploadId?: UploadId | null;
}

export interface GrantAgencyAccessToClientsBody {
    clients: Array<{
        clientId: ClientId;
        role: AgencyRole;
    }>;
}

export interface AgencyApiParams {
    agencyId: AgencyId;
}

export const AGENCIES_COLLECTION_API_ROOT = <T extends ObjectOrVoid>(path: PathBuilder<T>): PathBuilder<T> =>
    path.literal('agencies');
export const AGENCY_API_ROOT = <T extends AgencyApiParams>(path: PathBuilder<T>): PathBuilder<T> =>
    AGENCIES_COLLECTION_API_ROOT(path).param('agencyId');

export const AgencyApi = {
    createAgency: new ApiRoute<Agency, void, NewAgencyDto, void>(HttpMethod.Post, AGENCIES_COLLECTION_API_ROOT),
    deleteAgency: new ApiRoute<void, AgencyApiParams, void, void>(HttpMethod.Delete, AGENCY_API_ROOT),
    getAgencies: new ApiRoute<Agency[], void, void, void>(HttpMethod.Get, AGENCIES_COLLECTION_API_ROOT),
    getAgency: new ApiRoute<Agency, AgencyApiParams, void, void>(HttpMethod.Get, AGENCY_API_ROOT),
    getHasGoogleAdWordsMarketingContact: new ApiRoute<{ result: boolean }, void, void, void>(HttpMethod.Get, path =>
        path.literal('agency').literal('hasGoogleAdWordsMarketingContact'),
    ),
    getSpecialRequestDisclaimer: new ApiRoute<AgencySpecialRequestDisclaimer, void, void, void>(HttpMethod.Get, path =>
        path.literal('agency').literal('specialRequestDisclaimer'),
    ),
    grantAgencyAccessToClients: new ApiRoute<void, AgencyApiParams, GrantAgencyAccessToClientsBody, void>(
        HttpMethod.Post,
        path => AGENCY_API_ROOT(path).literal('clientAccess'),
    ),
    updateAgency: new ApiRoute<void, AgencyApiParams, UpdateAgencyDto, void>(HttpMethod.Patch, AGENCY_API_ROOT),
};

const AGENCY_USERS_COLLECTION_API_ROOT = <T extends AgencyApiParams>(path: PathBuilder<T>) =>
    AGENCY_API_ROOT(path).literal('users');
const AGENCY_USERS_INVITE_API_ENDPOINT = <T extends AgencyApiParams>(path: PathBuilder<T>) =>
    AGENCY_USERS_COLLECTION_API_ROOT(path).literal('invite');
const AGENCY_CLIENT_USERS_COLLECTION_API_ROOT = <T extends AgencyApiParams>(path: PathBuilder<T>) =>
    AGENCY_USERS_COLLECTION_API_ROOT(path).literal('clients');
const AGENCY_ORPHAN_CLIENT_USERS_COLLECTION_API_ROOT = <T extends AgencyApiParams>(path: PathBuilder<T>) =>
    AGENCY_CLIENT_USERS_COLLECTION_API_ROOT(path).literal('orphans');
const AGENCY_USER_API_ROOT = <T extends AgencyUsersApiParams>(path: PathBuilder<T>) =>
    AGENCY_USERS_COLLECTION_API_ROOT(path).param('userId');
interface AgencyUsersApiParams {
    agencyId: AgencyId;
    userId: UserId;
}
export const AgencyUsersApi = {
    deleteManager: new ApiRoute<void, AgencyUsersApiParams, void, void>(HttpMethod.Delete, AGENCY_USER_API_ROOT),
    getClientUsers: new ApiRoute<User[], AgencyApiParams, void, void>(
        HttpMethod.Get,
        AGENCY_CLIENT_USERS_COLLECTION_API_ROOT,
    ),
    getManager: new ApiRoute<User, AgencyUsersApiParams, void, void>(HttpMethod.Get, AGENCY_USER_API_ROOT),
    getManagers: new ApiRoute<User[], AgencyApiParams, void, void>(HttpMethod.Get, AGENCY_USERS_COLLECTION_API_ROOT),
    getOrphanClientUsers: new ApiRoute<User[], AgencyApiParams, void, void>(
        HttpMethod.Get,
        AGENCY_ORPHAN_CLIENT_USERS_COLLECTION_API_ROOT,
    ),
    inviteManager: new ApiRoute<SanitizedUser, AgencyApiParams, InviteAgencyUserDto, void>(
        HttpMethod.Post,
        AGENCY_USERS_INVITE_API_ENDPOINT,
    ),
    updateManager: new ApiRoute<User, AgencyUsersApiParams, UpdateManagerDto, void>(
        HttpMethod.Patch,
        AGENCY_USER_API_ROOT,
    ),
};

export type AgencyId = Brand<number, 'AgencyId'>;
export const AgencyId = createBranded<AgencyId>();
